/**
 * Utilidades para manejo y formateo de errores
 *
 * @module core/utils/error-helpers
 * @version 1.0
 *
 * @example
 * ```typescript
 * try {
 *   await api.create(data);
 * } catch (err) {
 *   const message = getErrorMessage(err);
 *   setError(message);
 * }
 * ```
 */

/**
 * Códigos de error HTTP comunes
 */
export const HTTP_ERRORS: Record<number, string> = {
  400: 'Datos inválidos. Por favor verifica la información ingresada',
  401: 'No estás autorizado. Por favor inicia sesión nuevamente',
  403: 'No tienes permisos para realizar esta acción',
  404: 'Recurso no encontrado',
  409: 'Conflicto: el recurso ya existe o está en uso',
  422: 'Datos no procesables. Verifica los campos requeridos',
  429: 'Demasiadas solicitudes. Por favor intenta nuevamente en unos momentos',
  500: 'Error del servidor. Por favor intenta nuevamente',
  502: 'Error de conexión con el servidor',
  503: 'Servicio no disponible temporalmente',
};

/**
 * Interfaz para error de Axios/API
 */
interface ApiError {
  response?: {
    status?: number;
    data?: {
      message?: string | string[];
      error?: string;
      statusCode?: number;
    };
  };
  message?: string;
}

/**
 * Extrae un mensaje de error legible desde una excepción
 *
 * @param error - Error capturado (any type desde catch)
 * @param fallback - Mensaje por defecto si no se puede extraer uno
 * @returns Mensaje de error formateado para el usuario
 *
 * @remarks
 * Prioridad de extracción:
 * 1. Backend message (response.data.message)
 * 2. Mensaje HTTP estándar basado en status code
 * 3. Error.message genérico
 * 4. Fallback proporcionado
 *
 * @example
 * ```typescript
 * catch (err) {
 *   const message = getErrorMessage(err, 'Error al guardar');
 *   // message = "El email ya está registrado" (desde backend)
 *   // o "Datos inválidos..." (desde HTTP_ERRORS)
 *   // o "Error al guardar" (fallback)
 * }
 * ```
 */
export function getErrorMessage(error: unknown, fallback = 'Error inesperado'): string {
  const apiError = error as ApiError;

  // 1. Intentar obtener mensaje del backend
  if (apiError.response?.data?.message) {
    const backendMessage = apiError.response.data.message;

    // Si es array de mensajes (validación múltiple), unir con saltos de línea
    if (Array.isArray(backendMessage)) {
      return backendMessage.join('\n');
    }

    return backendMessage;
  }

  // 2. Mensaje basado en status code HTTP
  if (apiError.response?.status) {
    const statusMessage = HTTP_ERRORS[apiError.response.status];
    if (statusMessage) {
      return statusMessage;
    }
  }

  // 3. Mensaje genérico del error
  if (apiError.message) {
    return apiError.message;
  }

  // 4. Fallback
  return fallback;
}

/**
 * Extrae el código de estado HTTP de un error
 *
 * @param error - Error capturado
 * @returns Status code o null si no disponible
 *
 * @example
 * ```typescript
 * const status = getErrorStatusCode(err);
 * if (status === 401) {
 *   // Redirigir a login
 * }
 * ```
 */
export function getErrorStatusCode(error: unknown): number | null {
  const apiError = error as ApiError;
  return apiError.response?.status ?? null;
}

/**
 * Verifica si un error es de tipo específico
 *
 * @param error - Error capturado
 * @param statusCode - Código HTTP a verificar
 * @returns true si el error coincide con el status code
 *
 * @example
 * ```typescript
 * if (isErrorType(err, 409)) {
 *   console.log('Conflicto: el slug ya existe');
 * }
 * ```
 */
export function isErrorType(error: unknown, statusCode: number): boolean {
  return getErrorStatusCode(error) === statusCode;
}

/**
 * Verifica si un error es de autenticación (401/403)
 *
 * @param error - Error capturado
 * @returns true si es error de autenticación
 */
export function isAuthError(error: unknown): boolean {
  const status = getErrorStatusCode(error);
  return status === 401 || status === 403;
}

/**
 * Verifica si un error es de validación (400/422)
 *
 * @param error - Error capturado
 * @returns true si es error de validación
 */
export function isValidationError(error: unknown): boolean {
  const status = getErrorStatusCode(error);
  return status === 400 || status === 422;
}

/**
 * Verifica si un error es de conflicto (409)
 *
 * @param error - Error capturado
 * @returns true si es error de conflicto (recurso duplicado)
 */
export function isConflictError(error: unknown): boolean {
  return getErrorStatusCode(error) === 409;
}
